import { useEffect, useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';

import { toTruthyMap } from '@sbv-clients/util-misc';

import { ALL_OPTIONS_VALUE } from './utilityTypes';

type AutoSelectIfEmptyType<OptionValueType, ValueType> = {
  value: OptionValueType;
  options: {
    value: ValueType;
  }[];
  selectFirstAsDefault?: boolean;
  multiSelect?: boolean;
  callback: (value: ValueType) => void;
  isSuccess?: boolean;
};

export const useAutoSelectValue = <ValueType, OptionValueType>({
  value,
  options,
  selectFirstAsDefault,
  multiSelect,
  callback,
  isSuccess,
}: AutoSelectIfEmptyType<ValueType, OptionValueType>) => {
  useEffect(() => {
    const firstOption = options?.[0]?.value;
    if (!selectFirstAsDefault || !firstOption) {
      return;
    }
    if (multiSelect) {
      if (isEmpty(value)) {
        callback(firstOption);
      }
    } else {
      if (typeof value === 'number' && value !== 0) {
        return;
      }
      callback(firstOption);
    }
  }, [options, selectFirstAsDefault, value, callback, multiSelect]);

  const byValueMap = useMemo(
    () => toTruthyMap(options.map((item) => item.value as string)),
    [options]
  );

  useEffect(() => {
    if (!value || !isSuccess) {
      return;
    }
    if (multiSelect) {
      const notMatched = (value as number[]).some(
        (value) => !byValueMap[value]
      );
      if (notMatched) {
        callback(ALL_OPTIONS_VALUE as any);
      }
    } else {
      if (!byValueMap[value as number]) {
        const firstOption = options?.[0]?.value;
        callback(firstOption);
      }
    }
    // eslint-disable-next-line
  }, [byValueMap, options, value]);
};

export default useAutoSelectValue;
